import { useEffect, useRef, useState } from "react";

/**
 * Lightweight site-wide toast. Any component can fire one via the
 * exported `fireToast()` helper — no context, no provider, no
 * dependency tree change. A single <Toast /> at the App root listens
 * for a custom event and renders the message.
 *
 * Used to acknowledge actions that otherwise complete in a new tab or
 * a separate app (Book CTA → Flybook, copy-email button, mailto:
 * launching the mail client). Mobile visitors on slow networks
 * benefit most — without the toast they tap, see nothing happen, and
 * tap again. With it: a brief gold pill confirms the action fired.
 */

type ToastDetail = {
  message: string;
  durationMs?: number;
};

const TOAST_EVENT = "ocoee-toast";

export function fireToast(message: string, durationMs = 2400) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<ToastDetail>(TOAST_EVENT, { detail: { message, durationMs } }),
  );
}

export function Toast() {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState("");
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const onToast = (e: Event) => {
      const ce = e as CustomEvent<ToastDetail>;
      const detail = ce.detail;
      if (!detail || !detail.message) return;

      // Clear any pending dismiss so consecutive fires don't cut each
      // other off prematurely.
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      setMessage(detail.message);
      setVisible(true);

      const dur = typeof detail.durationMs === "number" ? detail.durationMs : 2400;
      timerRef.current = window.setTimeout(() => {
        setVisible(false);
        timerRef.current = null;
      }, dur);
    };

    window.addEventListener(TOAST_EVENT, onToast);
    return () => {
      window.removeEventListener(TOAST_EVENT, onToast);
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <div
      className={`ocoee-toast ${visible ? "is-visible" : ""}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="ocoee-toast-msg">{message}</span>
    </div>
  );
}
